import { InfiniteQueryObserverResult, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import React, { createContext, useContext } from 'react'
import { useClient } from '@/stores/useClient.ts'
import AlbumGuestbookEntry from '@/models/albumGuestbookEntry.ts'
import { useApi } from '@/stores/useApi.ts'
import { HeyDrive } from '@/models/drive.ts'
import { AlbumFileType } from '@/models/albumFileType.ts'

export interface GuestbookEntryContext {
  // currentAlbumId: string | null
  guestbookEntries: Array<AlbumGuestbookEntry> | null
  hasNextPage?: boolean
  fetchNextPage: () => Promise<InfiniteQueryObserverResult>
  // @ts-ignore
  refetch: () => Promise<any, any>
  isLoading: boolean
  // updateGuestbookEntry: (guestbookEntry: AlbumGuestbookEntry) => void
  queryKey: Array<string>
  removeGuestbookEntry: (guestbookEntry: AlbumGuestbookEntry) => void
}

export const GuestbookEntryContext = createContext<GuestbookEntryContext | null>(null)

export function GuestbookEntryProvider({ children, drive, albumId }: { children: React.ReactNode, drive: HeyDrive, albumId: string  }) {
  const { getGuestbookEntries } = useClient()
  const queryClient = useQueryClient()
  const api = useApi()

  const queryKey = ['album', albumId, 'guestbook']
  const query = useInfiniteQuery({
    enabled: true,
    queryKey,
    async queryFn({ pageParam }) {
      if (pageParam) {
        return (await api.get(pageParam)).data
      }

      return (await getGuestbookEntries(albumId)).data
    },
    getNextPageParam(page) {
      // @ts-ignore
      return page.next
    },
    initialPageParam: undefined,
  })

  // function updateFile(updatedAlbumFile: AlbumFile) {
  //   // @ts-ignore
  //   queryClient.setQueryData(queryKey, (prev: any) => {
  //     if (!prev) {
  //       return undefined
  //     }
  //
  //     console.log('prev', prev)
  //     return {
  //       ...prev,
  //       pages: prev.pages.map((page: any) => ({
  //         ...page,
  //         results: page.results.map((file: AlbumFile) => file.id === updatedAlbumFile.id ? updatedAlbumFile : file)
  //       }))
  //     }
  //   })
  //   updateUploadedFile(updatedAlbumFile)
  // }

  function removeGuestbookEntry(guestbookEntry: AlbumGuestbookEntry) {
    queryClient.setQueryData(queryKey, (prev: any) => {
      if (!prev) {
        return undefined
      }

      return {
        ...prev,
        pages: prev.pages.map((page: any) => ({
          ...page,
          results: page.results.filter((entry: AlbumGuestbookEntry) => entry.id !== guestbookEntry.id)
        }))
      }
    })
  }

  function getDemoGuestbookEntries(): Array<AlbumGuestbookEntry> {
    return [
      {
        id: 'lalala',
        author_display_name: 'Dennis',
        author: 'demo',
        content: '\nHallo 😊 Hier ist Dennis von FridaySnap und ich wünsche dir viel Spaß mit der Demo!\n\nHör dir doch gerne meine Sprachnachricht an :)\n\n\n~ Team FridaySnap aus Frankfurt am Main',
        // content: 'Hi, hier ist Dennis von FridaySnap und das hier ist die Gästebuch Demo.\n' +
        //   'Hier können eure Gäste ganz persönliche Sprachmemos, Nachrichten und Bilder an euch senden.\n' +
        //   'Neben der Bildergalerie können eure Gäste euch hierüber auch ganz persönliche Sprachmemos, Nachrichten und Bilder an euch senden.\n.' +
        //   'In den Einstellungen könnt ihr das Gästebuch auch anpassen, es zum Beispiel auf privat stellen.\n' +
        //   'Falls ihr noch weitere Fragen habt, könnt ihr uns einfach über den WhatsApp Support oben auf der Seite schreiben ',
        audio: '/dennis-audio-guestbook-demo.mp3',
        files: [
          {
            detail: '/team-couch.jpg',
            id: '',
            author: '',
            author_display_name: '',
            file: '',
            file_type: AlbumFileType.IMAGE,
            file_name: '',
            content_type: '',
            thumbnail: null,
            game: null,
            file_creation: null,
            created_at: '',
            size: 0
          }
        ],
        is_demo: true,
        created_at: new Date(2025, 6, 2, 16, 12).toISOString(),
      }
    ]
  }

  const guestbookEntries: Array<AlbumGuestbookEntry> = [
    // @ts-ignore
    ...(query.data ? query.data.pages.flatMap(page => page.results) : []),
    ...(drive.is_demo ? getDemoGuestbookEntries() : []),
  ]


  return <GuestbookEntryContext.Provider value={{
    guestbookEntries,
    isLoading: query.isLoading,
    hasNextPage: query.hasNextPage,
    queryKey,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    removeGuestbookEntry,
  }}>
    { children }
  </GuestbookEntryContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGuestbookEntryContext() {
  return useContext(GuestbookEntryContext)
}
